/**
 * Print preview rendered through a portal into document.body, full screen or (with `dialog`)
 * as a centred modal card. While printing, every other top-level element is hidden — content
 * nested in #root would be hidden with it (same lesson as ReceiptPrintPreview). The chrome
 * (toolbar, backdrop) follows the app theme; the "paper" inside stays white, as it prints.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PrintPortalProps {
  title: string;
  /** CSS for @page, e.g. "letter" or "62mm 40mm" */
  pageSize: string;
  onClose: () => void;
  toolbar?: React.ReactNode;
  /** Small previews (one label) open as a modal card instead of covering the whole screen */
  dialog?: boolean;
  children: React.ReactNode;
}

export function PrintPortal({ title, pageSize, onClose, toolbar, dialog = false, children }: PrintPortalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      data-print-root
      className={cn(
        'fixed inset-0 z-[100] print:static print:bg-white',
        dialog ? 'flex items-center justify-center bg-black/60 p-4 print:block print:p-0' : 'overflow-y-auto bg-muted',
      )}
      onMouseDown={dialog ? (e) => e.target === e.currentTarget && onClose() : undefined}
    >
      <div
        role={dialog ? 'dialog' : undefined}
        aria-label={dialog ? title : undefined}
        className={cn(
          'flex flex-col bg-background text-foreground print:block print:bg-white',
          dialog
            ? 'max-h-full w-full max-w-4xl overflow-hidden rounded-lg border shadow-xl print:max-h-none print:overflow-visible print:rounded-none print:border-0 print:shadow-none'
            : 'min-h-full',
        )}
      >
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b bg-background px-4 py-2 print:hidden">
          <span className="font-medium">{title}</span>
          <div className="flex flex-wrap items-center gap-2">
            {toolbar}
            <Button size="sm" onClick={() => window.print()}>
              <Printer className="mr-2 size-4" /> Imprimir
            </Button>
            <Button size="icon" variant="ghost" onClick={onClose} aria-label="Cerrar">
              <X className="size-4" />
            </Button>
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col items-center gap-6 bg-muted py-6 print:block print:gap-0 print:bg-white print:p-0',
            dialog && 'overflow-y-auto',
          )}
        >
          {children}
        </div>
      </div>

      <style>{`
        @media print {
          body > *:not([data-print-root]) { display: none !important; }
          @page { size: ${pageSize}; margin: 0; }
          [data-print-page] { break-after: page; page-break-after: always; box-shadow: none !important; }
          [data-print-page]:last-child { break-after: auto; page-break-after: auto; }
        }
      `}</style>
    </div>,
    document.body,
  );
}
